import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CompanyRole } from "@/lib/miscellaneous/authorization";
import { USER_COMPANIES_QUERY } from "@/queries/companies/get-all";
import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FormValues } from "./formSchema";

interface UserAccessProps {
  form: UseFormReturn<FormValues>;
}

export function UserAccess({ form }: UserAccessProps) {
  const { data } = useQuery(USER_COMPANIES_QUERY);
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");

  const userType = form.watch("userType");
  const isCompanyUser = userType === "company";

  const companyAccess: { companyId: string; role: CompanyRole }[] =
    form.watch("companyAccess") || [];
  const companies = data?.userCompanies || [];

  const items: { id: string; label: string; description: string }[] =
    companies.map(
      (company: {
        companyId: string;
        companyName: string;
        description?: string;
      }) => ({
        id: company.companyId,
        label: company.companyName,
        description: company.description || "",
      }),
    );

  const filteredItems = useMemo(
    () =>
      items.filter(
        (item) =>
          item?.label?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item?.description?.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [items, searchValue],
  );

  const defaultRole: CompanyRole = isCompanyUser
    ? CompanyRole.CLIENT
    : CompanyRole.EDITOR;

  return (
    <div className="max-w-[800px] mx-auto">
      <h2 className="text-2xl font-semibold mb-6">{t("users.accesses")}</h2>
      <div className="space-y-4">
        <Input
          placeholder={t("users.search")}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="mb-2"
        />
        <div className="flex items-center space-x-2 mb-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              form.setValue(
                "companyAccess",
                items.map((i) => ({ companyId: i.id, role: defaultRole })),
                { shouldDirty: true },
              )
            }
          >
            {t("select_all")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              form.setValue("companyAccess", [], { shouldDirty: true })
            }
          >
            {t("clear_selection")}
          </Button>
        </div>

        <div className="border rounded-md divide-y h-64 overflow-y-auto">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div key={item.id} className="flex items-center px-3 py-2 gap-3">
                <Checkbox
                  checked={companyAccess.some((a) => a.companyId === item.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      const next = [...companyAccess];
                      if (!next.some((a) => a.companyId === item.id)) {
                        next.push({ companyId: item.id, role: defaultRole });
                      }
                      form.setValue("companyAccess", next, {
                        shouldDirty: true,
                      });
                    } else {
                      form.setValue(
                        "companyAccess",
                        companyAccess.filter((a) => a.companyId !== item.id),
                        { shouldDirty: true },
                      );
                    }
                  }}
                />
                <div className="flex-1">
                  <div className="font-medium">{item.label}</div>
                  {item.description && (
                    <div className="text-sm text-gray-500">
                      {item.description}
                    </div>
                  )}
                </div>
                {isCompanyUser && (
                  <div className="w-56">
                    <FormField
                      control={form.control}
                      name={"companyAccess" as unknown as never}
                      render={() => (
                        <FormItem>
                          <Select
                            onValueChange={(value) => {
                              const next = companyAccess.map((a) =>
                                a.companyId === item.id
                                  ? { ...a, role: value as CompanyRole }
                                  : a,
                              );
                              form.setValue("companyAccess", next, {
                                shouldDirty: true,
                              });
                            }}
                            value={
                              companyAccess.find((a) => a.companyId === item.id)
                                ?.role
                            }
                            disabled={
                              !companyAccess.some(
                                (a) => a.companyId === item.id,
                              )
                            }
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t("users.select_role")}
                              />
                            </SelectTrigger>
                            <SelectContent className="z-[1000]">
                              <SelectItem value={CompanyRole.CLIENT}>
                                {t("users.client")}
                              </SelectItem>
                              <SelectItem
                                value={CompanyRole.CLIENT_SELF_BOOKING}
                              >
                                {t("users.client_self_booking")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">
              {t("no_users_found")}
            </div>
          )}
        </div>

        <FormField
          control={form.control}
          name={"companyAccess" as unknown as never}
          render={() => (
            <FormItem>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
